import React from 'react';
import type { PermissionsPolicy, PostureInstance } from './types';

type ApplyStep = 'target' | 'preflight' | 'preview' | 'confirm' | 'running' | 'done';
type TargetMode = 'all' | 'pilot' | 'pick';

type PreflightDecision = 'authorised' | 'unauthorised' | 'inspector_not_granted';

interface PreflightResult {
  instanceId: string;
  siteUrl: string;
  decision: PreflightDecision;
  reason?: string;
}

type DeltaKind = 'group_create' | 'member_add' | 'member_remove' | 'lockout_warning';

interface ProjectionDelta {
  kind: DeltaKind;
  group: string;
  who?: string;
  source?: 'baseline' | 'tenant';
}

interface InstanceProjection {
  instanceId: string;
  siteUrl: string;
  deltas: ProjectionDelta[];
}

interface ProjectionPreview {
  authorisedCount: number;
  groupCreates: number;
  memberAdds: number;
  memberRemoves: number;
  lockoutWarnings: number;
  perInstance: InstanceProjection[];
}

interface ApplyRunInstance {
  instanceId: string;
  siteUrl: string;
  status: 'success' | 'partial' | 'failed' | 'skipped' | 'in_progress';
  message?: string;
  succeededDeltas: number;
  failedDeltas: number;
  skippedDeltas: number;
}

interface ApplyRun {
  runId: string;
  tenantId: string;
  startedAt: string;
  completedAt?: string;
  actor: string;
  reason: string;
  policyVersionNumber?: number;
  status: 'pending' | 'running' | 'succeeded' | 'partial' | 'failed';
  succeeded: number;
  failed: number;
  skipped: number;
  instances: ApplyRunInstance[];
}

interface ApplyTabProps {
  tenantId: string;
  policy: PermissionsPolicy;
  instances: PostureInstance[];
  /**
   * When set (e.g. Drift's "Fix in Apply"), the flow starts scoped to this
   * single installation instead of defaulting to all installations. A new
   * object identity re-applies the focus even for the same instance.
   */
  focus?: { instanceId: string } | null;
}

const PRE_DECISION_LABEL: Record<PreflightDecision, string> = {
  authorised: '✓ authorised',
  unauthorised: '⚠ unauthorised',
  inspector_not_granted: '⚠ inspector not granted'
};

const prettyHost = (siteUrl: string): string => {
  try {
    const parsed = new URL(siteUrl);
    return parsed.host + parsed.pathname;
  } catch {
    return siteUrl;
  }
};

const generateIdempotencyKey = (): string => {
  const cryptoLike = (globalThis as typeof globalThis & { crypto?: { randomUUID?: () => string } }).crypto;
  if (cryptoLike?.randomUUID) {
    return cryptoLike.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const ApplyTab: React.FC<ApplyTabProps> = ({ tenantId, policy, instances, focus }) => {
  const [step, setStep] = React.useState<ApplyStep>('target');
  const [targetMode, setTargetMode] = React.useState<TargetMode>('all');
  const [pickedInstanceIds, setPickedInstanceIds] = React.useState<string[]>([]);
  const [preflight, setPreflight] = React.useState<PreflightResult[] | null>(null);
  const [preview, setPreview] = React.useState<ProjectionPreview | null>(null);
  const [policyVersionNumber, setPolicyVersionNumber] = React.useState<number | undefined>();
  const [policySource, setPolicySource] = React.useState<'published' | 'draft' | undefined>();
  const [planHash, setPlanHash] = React.useState<string | undefined>();
  const [run, setRun] = React.useState<ApplyRun | null>(null);
  const [reason, setReason] = React.useState('');
  const [lockoutAcknowledged, setLockoutAcknowledged] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [history, setHistory] = React.useState<ApplyRun[]>([]);

  // Stop TS lint about unused props; the policy is consumed by the server, not here.
  void policy;

  const eligibleInstances = React.useMemo(
    () => instances.filter((entry) => entry.status !== 'deregistered'),
    [instances]
  );

  const resolvedInstances = React.useMemo(() => {
    if (targetMode === 'all') return eligibleInstances;
    if (targetMode === 'pilot') return eligibleInstances.slice(0, 3);
    return eligibleInstances.filter((entry) => pickedInstanceIds.includes(entry.instanceId));
  }, [eligibleInstances, targetMode, pickedInstanceIds]);

  const loadHistory = React.useCallback(async (): Promise<void> => {
    if (!tenantId) return;
    try {
      const params = new URLSearchParams({ tenantId });
      const response = await fetch(`/api/permissions/projection/runs?${params.toString()}`);
      if (!response.ok) return;
      const body = await response.json() as { runs?: ApplyRun[] };
      setHistory(Array.isArray(body.runs) ? body.runs : []);
    } catch {
      // History errors are non-fatal — leave list empty.
    }
  }, [tenantId]);

  React.useEffect(() => { void loadHistory(); }, [loadHistory]);

  const runPreflight = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const response = await fetch('/api/permissions/projection/preflight', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          tenantId,
          targetInstanceIds: resolvedInstances.map((entry) => entry.instanceId)
        })
      });
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(payload.message ?? `Preflight failed (${response.status})`);
      }
      const body = await response.json() as { results: PreflightResult[] };
      setPreflight(body.results);
      setStep('preflight');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const runPreview = async (): Promise<void> => {
    if (!preflight) return;
    setBusy(true);
    setError(null);
    try {
      const authorisedIds = preflight
        .filter((entry) => entry.decision === 'authorised')
        .map((entry) => entry.instanceId);
      const response = await fetch('/api/permissions/projection/preview', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ tenantId, targetInstanceIds: authorisedIds })
      });
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(payload.message ?? `Preview failed (${response.status})`);
      }
      const body = await response.json() as {
        preview: ProjectionPreview;
        policyVersionNumber?: number;
        policySource?: 'published' | 'draft';
        planHash?: string;
      };
      setPreview(body.preview);
      setPolicyVersionNumber(body.policyVersionNumber);
      setPolicySource(body.policySource);
      setPlanHash(body.planHash);
      setStep('preview');
      setReason('');
      setLockoutAcknowledged(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const goToConfirm = (): void => {
    setStep('confirm');
  };

  const runApply = async (): Promise<void> => {
    if (!preview || !planHash) return;
    setBusy(true);
    setError(null);
    setStep('running');
    try {
      const response = await fetch('/api/permissions/projection/apply', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          tenantId,
          reason,
          targetInstanceIds: preview.perInstance.map((entry) => entry.instanceId),
          acceptLockoutWarnings: lockoutAcknowledged,
          idempotencyKey: generateIdempotencyKey(),
          planHash
        })
      });
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { errorCode?: string; message?: string };
        if (payload.errorCode === 'PROJECTION_PLAN_CHANGED') {
          // The server-side plan no longer matches what was reviewed —
          // discard the stale preview and send the operator back to re-check.
          setPreview(null);
          setPlanHash(undefined);
          setStep('preflight');
          throw new Error(payload.message ?? 'The plan changed since preview. Run Preview impact again.');
        }
        throw new Error(payload.message ?? `Apply failed (${response.status})`);
      }
      const body = await response.json() as { run: ApplyRun };
      setRun(body.run);
      setStep('done');
      void loadHistory();
    } catch (err) {
      setError((err as Error).message);
      setStep((current) => (current === 'running' ? 'confirm' : current));
    } finally {
      setBusy(false);
    }
  };

  const reset = (): void => {
    setStep('target');
    setPreflight(null);
    setPreview(null);
    setPolicyVersionNumber(undefined);
    setPolicySource(undefined);
    setPlanHash(undefined);
    setRun(null);
    setReason('');
    setLockoutAcknowledged(false);
    setError(null);
  };

  React.useEffect(() => {
    if (!focus?.instanceId) return;
    // Scope the flow to the focused installation and discard any in-flight
    // wizard state — "Fix in Apply" must never inherit an all-sites target.
    reset();
    setTargetMode('pick');
    setPickedInstanceIds([focus.instanceId]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus]);

  const authorisedCount = preflight?.filter((entry) => entry.decision === 'authorised').length ?? 0;
  const unauthorisedCount = preflight?.filter((entry) => entry.decision === 'unauthorised').length ?? 0;
  const inspectorMissingCount = preflight?.filter((entry) => entry.decision === 'inspector_not_granted').length ?? 0;
  const lockoutWarnings = preview?.lockoutWarnings ?? 0;
  const canSubmitApply =
    step === 'confirm'
    && reason.trim().length > 0
    && (lockoutWarnings === 0 || lockoutAcknowledged)
    && !!planHash
    && !busy;

  return (
    <div className="permissions-apply-tab">
      <div className="apply-header">
        <h3>Apply</h3>
        <p className="card-intro">
          Walks target → preflight → preview → confirm → progress. Apply writes through to each
          authorised site (group ensure + member add). Member removals come next; current scope is
          additive.
        </p>
      </div>

      {error && <div className="permissions-error" role="alert">{error}</div>}

      <section className="apply-step">
        <h4>1. Choose target</h4>
        <div className="apply-target-options">
          <label>
            <input
              type="radio"
              name="apply-target"
              value="all"
              checked={targetMode === 'all'}
              onChange={() => setTargetMode('all')}
            />
            <span>All installations ({eligibleInstances.length})</span>
          </label>
          <label>
            <input
              type="radio"
              name="apply-target"
              value="pilot"
              checked={targetMode === 'pilot'}
              onChange={() => setTargetMode('pilot')}
            />
            <span>Pilot group (first {Math.min(3, eligibleInstances.length)})</span>
          </label>
          <label>
            <input
              type="radio"
              name="apply-target"
              value="pick"
              checked={targetMode === 'pick'}
              onChange={() => setTargetMode('pick')}
            />
            <span>Pick installations</span>
          </label>
        </div>
        {targetMode === 'pick' && (
          <select
            className="input"
            multiple
            size={Math.min(6, eligibleInstances.length)}
            value={pickedInstanceIds}
            onChange={(event) => {
              const next = Array.from(event.target.selectedOptions).map((option) => option.value);
              setPickedInstanceIds(next);
            }}
          >
            {eligibleInstances.map((instance) => (
              <option key={instance.instanceId} value={instance.instanceId}>
                {prettyHost(instance.siteUrl)}
              </option>
            ))}
          </select>
        )}
        <div className="apply-step-actions">
          <button
            type="button"
            className="btn"
            disabled={resolvedInstances.length === 0 || busy || step === 'running'}
            onClick={() => void runPreflight()}
          >
            {busy && step === 'target' ? 'Checking…' : `Check rights on ${resolvedInstances.length} site${resolvedInstances.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </section>

      {preflight && (step === 'preflight' || step === 'preview' || step === 'confirm' || step === 'running' || step === 'done') && (
        <section className="apply-step">
          <h4>2. Per-site rights pre-check</h4>
          <p className="apply-step-summary">
            <strong>{authorisedCount}</strong> can be changed,{' '}
            <strong>{unauthorisedCount}</strong> will be skipped,{' '}
            <strong>{inspectorMissingCount}</strong> not yet granted to inspector.
          </p>
          <ul className="apply-preflight-list">
            {preflight.map((entry) => (
              <li key={entry.instanceId} className={`apply-preflight-row decision-${entry.decision}`}>
                <span className="apply-preflight-host">{prettyHost(entry.siteUrl)}</span>
                <span className="apply-preflight-decision">{PRE_DECISION_LABEL[entry.decision]}</span>
                {entry.reason && <span className="apply-preflight-reason muted">{entry.reason}</span>}
              </li>
            ))}
          </ul>
          {step === 'preflight' && (
            <div className="apply-step-actions">
              <button type="button" className="btn secondary" onClick={reset} disabled={busy}>Back</button>
              <button
                type="button"
                className="btn"
                disabled={authorisedCount === 0 || busy}
                onClick={() => void runPreview()}
              >
                {busy ? 'Computing…' : 'Preview impact'}
              </button>
            </div>
          )}
        </section>
      )}

      {preview && (step === 'preview' || step === 'confirm' || step === 'running' || step === 'done') && (
        <section className="apply-step">
          <h4>
            3. Preview impact
            {policyVersionNumber ? ` (policy v${policyVersionNumber})` : policySource === 'draft' ? ' (draft policy — nothing published yet)' : ''}
          </h4>
          <pre className="apply-preview-summary">
            {`Across ${preview.authorisedCount} authorised site${preview.authorisedCount === 1 ? '' : 's'}:
  + ${preview.groupCreates} group${preview.groupCreates === 1 ? '' : 's'} to create
  + ${preview.memberAdds} member${preview.memberAdds === 1 ? '' : 's'} to add
  − ${preview.memberRemoves} member${preview.memberRemoves === 1 ? '' : 's'} to remove
  ⚠ ${preview.lockoutWarnings} lock-out warning${preview.lockoutWarnings === 1 ? '' : 's'}`}
          </pre>
          <details className="apply-preview-details">
            <summary>Per-site detail</summary>
            <ul className="apply-preview-instance-list">
              {preview.perInstance.map((projection) => (
                <li key={projection.instanceId}>
                  <strong>{prettyHost(projection.siteUrl)}</strong>
                  {projection.deltas.length === 0 ? (
                    <span className="muted"> — no changes</span>
                  ) : (
                    <ul className="apply-preview-delta-list">
                      {projection.deltas.map((delta, idx) => (
                        <li key={idx} className={`apply-delta apply-delta-${delta.kind}`}>
                          {delta.kind === 'group_create' && <span>+ create group <strong>{delta.group}</strong></span>}
                          {delta.kind === 'member_add' && <span>+ add <strong>{delta.who}</strong> to {delta.group}</span>}
                          {delta.kind === 'member_remove' && <span>− remove <strong>{delta.who}</strong> from {delta.group}</span>}
                          {delta.kind === 'lockout_warning' && <span>⚠ {delta.who} — {delta.group}</span>}
                          {delta.source && <span className="apply-delta-source"> [{delta.source}]</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </details>
          {step === 'preview' && (
            <div className="apply-step-actions">
              <button type="button" className="btn secondary" onClick={reset}>Discard</button>
              <button type="button" className="btn" onClick={goToConfirm}>Continue to confirm</button>
            </div>
          )}
        </section>
      )}

      {(step === 'confirm' || step === 'running' || step === 'done') && preview && (
        <section className="apply-step apply-confirm">
          <h4>4. Confirm and apply</h4>
          <label className="apply-confirm-row">
            <span className="apply-confirm-label">Reason for this apply</span>
            <input
              className="input"
              type="text"
              placeholder="e.g. Added new auditor Susan; quarterly governance sync"
              value={reason}
              disabled={step !== 'confirm' || busy}
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
          {lockoutWarnings > 0 && (
            <label className="apply-confirm-row apply-confirm-lockout">
              <input
                type="checkbox"
                checked={lockoutAcknowledged}
                disabled={step !== 'confirm' || busy}
                onChange={(event) => setLockoutAcknowledged(event.target.checked)}
              />
              <span>
                I have reviewed the <strong>{lockoutWarnings}</strong> lock-out warning{lockoutWarnings === 1 ? '' : 's'} and accept the risk.
              </span>
            </label>
          )}
          {step === 'confirm' && (
            <div className="apply-step-actions">
              <button type="button" className="btn secondary" onClick={reset}>Cancel</button>
              <button
                type="button"
                className="btn"
                disabled={!canSubmitApply}
                onClick={() => void runApply()}
              >
                {busy ? 'Applying…' : `Apply to ${preview.authorisedCount} site${preview.authorisedCount === 1 ? '' : 's'}`}
              </button>
            </div>
          )}
        </section>
      )}

      {(step === 'running' || step === 'done') && (
        <section className="apply-step">
          <h4>5. Progress{run?.status ? ` — ${run.status}` : ''}</h4>
          {step === 'running' && !run && <div className="muted">Running apply…</div>}
          {run && (
            <ul className="apply-progress-list">
              {run.instances.map((row) => (
                <li key={row.instanceId} className={`apply-progress-row status-${row.status}`}>
                  <span className="apply-progress-host">{prettyHost(row.siteUrl)}</span>
                  <span className="apply-progress-status">{row.status}</span>
                  <span className="apply-progress-counts muted">
                    {row.succeededDeltas} ok · {row.failedDeltas} failed · {row.skippedDeltas} skipped
                  </span>
                  {row.message && <span className="apply-progress-message muted">{row.message}</span>}
                </li>
              ))}
            </ul>
          )}
          {step === 'done' && (
            <div className="apply-step-actions">
              <button type="button" className="btn secondary" onClick={reset}>Start another</button>
            </div>
          )}
        </section>
      )}

      <section className="apply-step apply-history-section">
        <h4>Apply history</h4>
        {history.length === 0 ? (
          <span className="muted">No applies recorded yet.</span>
        ) : (
          <ul className="apply-history-list">
            {history.map((entry) => (
              <li key={entry.runId} className="apply-history-row">
                <span className="apply-history-when">{new Date(entry.startedAt).toLocaleString()}</span>
                <span className="apply-history-actor">{entry.actor}</span>
                <span className="apply-history-reason muted">{entry.reason}</span>
                <span className="apply-history-counts">
                  {entry.succeeded} ok · {entry.failed} failed · {entry.skipped} skipped
                  {typeof entry.policyVersionNumber === 'number' ? ` · policy v${entry.policyVersionNumber}` : ''}
                  {` · ${entry.status}`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default ApplyTab;
